/**
 * web ↔ 原生通信层。
 *
 * 协议以 App 侧《成绩页 H5 ↔ 原生 桥接文档 v1.0.0》为准：
 * 统一走 window.WHBridge.call(action, params, options)，返回 Promise。
 * 本版本原生不会反向调用 H5，所以不注册任何监听（文档 §7）。
 *
 * 页面里所有"要跳出本页"的动作都调这里的封装，由本文件决定是发给原生还是走
 * 普通路由跳转。分叉只发生在这一个文件，页面组件对端内/端外无感知 ——
 * 这是"一套代码三端用"能成立的前提。
 */
import { isEmbed, getEmbedParams } from './env'
import { isNewHsk16 } from '@/utils/reportRoute'

// 文档 §5 的框架错误码，取用到的几个
export const ERR = {
  UNKNOWN: -1000,
  TIMEOUT: -1001,
  BRIDGE_UNAVAILABLE: -1002,
  ACTION_NOT_REGISTERED: -1003,
  BAD_PARAMS: -1004,
  NO_PERMISSION: -1005,
  PAGE_RELEASED: -1006,
  PAGE_NAVIGATED: -1008,
  // 业务错误码（正数）
  RETAKE_FETCH_FAILED: 2001,
  QUESTION_NOT_FOUND: 2002,
  PRICE_NOT_IMPLEMENTED: 2100,
}

export const ACTION = {
  SET_TITLE: 'WHCommon.setTitle',
  CLOSE: 'WHCommon.close',
  LOGOUT: 'WHCommon.logout',
  RETAKE: 'WHExam.retakeExam',
  VIEW_QUESTION: 'WHExam.viewQuestion',
  // ⚠️ 在 WHCommon 而不是 WHExam 命名空间下（早期文档写的是 WHExam.getProductPrice，
  // 与原生实际实现不符）
  GET_PRODUCT_PRICE: 'WHCommon.getProductPrice',
  START_PAYMENT: 'WHCommon.startPayment',
}

/**
 * 纯 web（浏览器）下的降级实现。
 *
 * 这张表是"同一份代码在浏览器里也能跑"的全部秘密：isEmbed() 为 false 时
 * 一行原生代码都不执行，各动作退回改造前的路由行为。
 */
const webFallback = {
  [ACTION.SET_TITLE]: ({ title }) => {
    if (title) document.title = title
  },
  [ACTION.CLOSE]: (params, { router }) => {
    if (router) router.back()
  },
  // 端外的退出登录走项目既有的 vuex handleLogout，不从 bridge 走，
  // 所以这里是 noop（不是遗漏）
  [ACTION.LOGOUT]: () => {},
  [ACTION.RETAKE]: (params, { router }) => {
    if (!router) return
    // examination_id / ansStatus / paperStatus 三个 falsy 值决定了走
    // initExaminationPaper 而不是 continueTest（simulation.vue:626-653）。
    // 传成真值会"续上刚考完那场"：用户看到已作答的卷子和残余倒计时，
    // 交卷后覆盖原成绩。
    router.replace({
      name: 'Introduce',
      query: {
        ...((params && params.query) || {}),
        examination_id: '',
        ansStatus: 0,
        paperStatus: null,
      },
    })
  },
  /**
   * 浏览器里点题号：跳到考试页看单题，与老页 resultReport.vue:507-530 同一套参数。
   *
   * 不发任何请求 —— 目标页靠 paperStatus=1 走 getFinishedRes 自己拉已完成的答卷
   * （simulation.vue:626-628）。三个参数缺一不可：
   *   currentQuesIndex  要看的题号
   *   paperStatus: 1    进"查看"模式而不是答题模式（否则会当成继续考试）
   *   report: true      标记来源是成绩报告
   */
  [ACTION.VIEW_QUESTION]: ({ questionNo, levelId, query }, { router }) => {
    if (!router) return
    // 本页只服务 newhsk1-6，对应的考试页固定是 SimulationTri
    // （Introduce.vue:833-836 的映射同源）。等级异常时不跳，避免落到错的模板上。
    if (!isNewHsk16(levelId)) return
    router.push({
      name: 'SimulationTri',
      query: {
        ...(query || {}),
        currentQuesIndex: questionNo,
        paperStatus: 1,
        report: true,
      },
    })
  },
  [ACTION.GET_PRODUCT_PRICE]: () => {
    // 端外不走原生内购，价格由接口下发
    return null
  },
  // 端外的支付由页面自己走 web 收银链路（下单 → 拉起 → 轮询），
  // 不经过 bridge，所以这里只是个空实现占位
  [ACTION.START_PAYMENT]: () => null,
}

/**
 * 调用一条原生能力。
 *
 * @returns {Promise<{ok:boolean, data?:any, code?:number, message?:string}>}
 *          **永不 reject** —— 调用方只判 ok，不必到处包 try/catch。
 *          原生侧的 reject 会被转成 { ok:false, code, message }。
 */
export function call(action, params = {}, opts = {}) {
  const { router = null, timeout } = opts

  if (!isEmbed()) {
    const fb = webFallback[action]
    const data = fb ? fb(params, { router }) : undefined
    return Promise.resolve({ ok: true, degraded: true, data })
  }

  // 调试模式（?_mockbridge=1）：没有真原生，把报文打到 console 再回成功
  if (process.env.NODE_ENV !== 'production' && getEmbedParams().isMock && !window.WHBridge) {
    console.log('[bridge → native]', action, params)
    return Promise.resolve({ ok: true, mocked: true, data: {} })
  }

  const options = timeout ? { timeout } : undefined
  try {
    return window.WHBridge.call(action, params || {}, options).then(
      (data) => ({ ok: true, data: data || {} }),
      (err) => ({
        ok: false,
        code: (err && err.code) != null ? err.code : ERR.UNKNOWN,
        message: (err && err.message) || '',
      })
    )
  } catch (e) {
    // WHBridge 存在但 call 抛了同步异常（协议不匹配 / 老版本 App）
    return Promise.resolve({ ok: false, code: ERR.BRIDGE_UNAVAILABLE, message: String(e) })
  }
}

/** 设置原生导航栏标题。多语言由 H5 负责，原生不翻译（文档 §4.1） */
export function setTitle(title) {
  if (!title) return Promise.resolve({ ok: false, code: ERR.BAD_PARAMS })
  return call(ACTION.SET_TITLE, { title })
}

/** 关闭当前页面。原生决定返回目标，H5 无法干预（文档 §4.2） */
export function close(reason) {
  // reason 原生当前不消费，带上是为了原生日志里能看出是谁关的
  return call(ACTION.CLOSE, reason ? { reason } : {})
}

/**
 * 退出登录：原生会直接跳到 App 的登录页。
 *
 * 登录态失效时用它而不是 close —— close 只是把页面关掉，用户回到上一页后
 * 继续点什么都是失败的，且没有任何解释；logout 把用户送到能解决问题的地方。
 */
export function logout() {
  return call(ACTION.LOGOUT, {})
}

/**
 * 再考一遍。
 *
 * 原生自己持有跳转所需的等级与时长，不需要 H5 传参（文档 §4.3）。
 * 失败时原生不弹任何提示，必须由 H5 展示错误。
 * 放宽超时：全真模拟要先拉成绩数据再跳准备考试页，默认 15s 偏紧。
 */
export function retakeExam(opts = {}) {
  return call(ACTION.RETAKE, {}, { timeout: 30000, ...opts })
}

/**
 * 查看某道题。
 *
 * questionNo 传**考生可见的题号**（页面上显示的那个数字）。
 * 题号 → 答题页序号的换算由原生做，H5 不要自己算：一段听力对话下挂多个题号时
 * 它们同属一页，两者并不相等（文档 §4.4）。
 */
export function viewQuestion(questionNo, opts = {}) {
  // levelId / query 只有端外的降级跳转会用到，端内原生自己持有这些信息，
  // 多传两个字段不影响它（原生只读 questionNo）
  const { levelId, query, ...rest } = opts
  return call(ACTION.VIEW_QUESTION, { questionNo, levelId, query }, rest)
}

/**
 * 端内发起支付。iOS 走 StoreKit 内购，Android 按渠道走 Play Billing 或原生收银台
 * —— 挑哪条由原生决定，web 只把四个商品 id 一起交出去。
 *
 * 回参只有 `paySuccess`（true / false）：true 就重新拉报告显示完整内容。
 *
 * 放宽超时到 5 分钟：内购要弹系统面板、可能要输密码/验指纹，甚至走
 * Ask to Buy 家长审批，默认 15s 远远不够。
 */
export function startPayment(params, opts = {}) {
  return call(ACTION.START_PAYMENT, params, { timeout: 300000, ...opts })
}

/**
 * 取 iOS 内购的本地化价格（仅 iOS 需要，Android 用后端接口下发的价格）。
 *
 * App Store 审核要求展示价必须来自 StoreKit 的 displayPrice —— 拿服务端金额
 * 显示会命中 2.3.1 价格误导。返回 { price, currency }。
 */
export function getProductPrice(appleProductId, opts = {}) {
  return call(ACTION.GET_PRODUCT_PRICE, { appleProductId }, opts)
}

export { isEmbed, getEmbedParams }

export default {
  call,
  setTitle,
  close,
  logout,
  retakeExam,
  startPayment,
  getProductPrice,
  viewQuestion,
  isEmbed,
  getEmbedParams,
  ACTION,
  ERR,
}
